import { CircleAlert, CircleCheck, Info, TriangleAlert, X } from 'lucide-react';
import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/shared/lib/cn';
import { IconButton } from './IconButton';
import { ToastContext, type ToastContextValue, type ToastOptions, type ToastTone } from './toast-context';

interface ActiveToast extends ToastOptions {
  id: string;
  tone: ToastTone;
}

const toneIcons = {
  info: Info,
  success: CircleCheck,
  warning: TriangleAlert,
  danger: CircleAlert,
} as const;

const toneClasses: Record<ToastTone, string> = {
  info: 'border-info-100 bg-info-50',
  success: 'border-success-100 bg-success-50',
  warning: 'border-warning-100 bg-warning-50',
  danger: 'border-danger-100 bg-danger-50',
};

const iconClasses: Record<ToastTone, string> = {
  info: 'text-info-500',
  success: 'text-success-500',
  warning: 'text-warning-500',
  danger: 'text-danger-500',
};

/**
 * Dostarcza komunikaty ulotne (toasty) calej aplikacji.
 *
 * Komunikaty renderujemy przez portal do `document.body`, zeby nie dawaly sie
 * przyciac przez `overflow: hidden` na ktoryms z kontenerow.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ActiveToast[]>([]);
  const counterRef = useRef(0);
  const timersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const dismissToast = useCallback((id: string) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(
    (options: ToastOptions) => {
      counterRef.current += 1;
      const id = `toast-${counterRef.current}`;
      const duration = options.duration ?? 5000;

      setToasts((current) => [...current, { ...options, id, tone: options.tone ?? 'info' }]);

      if (duration > 0) {
        timersRef.current.set(
          id,
          setTimeout(() => dismissToast(id), duration),
        );
      }

      return id;
    },
    [dismissToast],
  );

  const value = useMemo<ToastContextValue>(
    () => ({ showToast, dismissToast }),
    [showToast, dismissToast],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}

      {typeof document !== 'undefined' &&
        createPortal(
          <div
            role="region"
            aria-label="Powiadomienia"
            className="pointer-events-none fixed inset-x-0 bottom-0 z-[100] flex flex-col items-center gap-2 p-4 sm:items-end"
          >
            {toasts.map((toast) => {
              const ToneIcon = toneIcons[toast.tone];

              return (
                <div
                  key={toast.id}
                  role={toast.tone === 'danger' ? 'alert' : 'status'}
                  className={cn(
                    'pointer-events-auto flex w-full max-w-sm items-start gap-3',
                    'rounded-xl border p-3.5 shadow-lg',
                    toneClasses[toast.tone],
                  )}
                >
                  <ToneIcon
                    aria-hidden="true"
                    className={cn('mt-px size-[1.125rem] shrink-0', iconClasses[toast.tone])}
                  />

                  <div className="min-w-0 flex-1">
                    {toast.title && (
                      <p className="text-fg-strong text-sm font-semibold">{toast.title}</p>
                    )}
                    <p className="text-fg-muted text-sm">{toast.description}</p>
                  </div>

                  <IconButton
                    icon={<X />}
                    label="Zamknij powiadomienie"
                    size="sm"
                    onClick={() => dismissToast(toast.id)}
                  />
                </div>
              );
            })}
          </div>,
          document.body,
        )}
    </ToastContext.Provider>
  );
}
